/**
 * Xlsx mini build is untyped so ts-ignore is added to satisfy compiler
 * https://github.com/SheetJS/sheetjs/issues/1999
 *
 * Mini build is used because it is 212KB vs 1.2MB
 * for the whole build
 */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
import XLSX from "xlsx/dist/xlsx.mini.min.js";
import type { WorkSheet } from "xlsx";
import { LengthUnits } from "@/libs/units/length";
import { PressureUnits } from "@/libs/units/pressure";
import { TemperatureUnits } from "@/libs/units/temperature";
import { FlowRateUnits } from "@/libs/units/flowRate";
import type { Casings, Tubings } from "gql/api";
import type {
  Equipment,
  OperatingParameters,
  Scenario,
  WellProperties,
  FormItems
} from "@/composables/forms/formTypes";
import type { ConvertableUnit } from "@/libs/units";
import { convert } from "@/libs/units";
import { LiquidRatioUnits } from "@/libs/units/liquidRatio";
export async function parseWellDataSheet(
  file: File,
  casingSearch: Casings[] | undefined,
  tubingSearch: Tubings[] | undefined
) {
  const scenarioFormData: Partial<Scenario> = {};
  const parametersFormData: Partial<OperatingParameters> = {};
  const wellPropertiesFormData: Partial<WellProperties> = {};
  const equipmentFormData: Partial<Equipment> = {};
  // Use the xlsx parsing library to read the file
  const buffer = await file.arrayBuffer();
  const allCells = XLSX.read(buffer).Sheets.Data;
  if (!validate(allCells)) {
    throw new TypeError("Invalid file format");
  }
  const unitsMap = {
    lengthUnit: "C11",
    liquidFlowUnit: "H11",
    gasFlowUnit: "H12",
    pressureUnit: "H13",
    temperatureUnit: "H14"
  };
  const wellDataMap = {
    iprModel: "C14",
    viscosityTest: "C35",
    viscosityTestTemp: "C36",
    packerAbovePump: "C37",
    surfaceController: "H30",
    surfaceVoltage: "H31",
    casingSize: "C16",
    tubing: "C17",
    topPerfs: "C18",
    tubingLength: "C19",
    tempAtDatum: "H16",
    tempAtWellHead: "H17",
    tubingPressure: "H18",
    casingPressure: "H19",
    oilGravity: "C21",
    waterGravity: "C22",
    gasGravity: "C23",
    gasRate: "C24",
    percentWaterCut: "C25",
    percentH2S: "C26",
    percentCO2: "C27",
    percentN2: "C28",
    staticPr: "H21",
    producingPwf: "H22",
    datumPoint: "H23",
    testLiquidRate: "H24",
    desiredFlow: "H25",
    bubblePointPressure: "H26",
    corrosive: "C30",
    scale: "C31",
    paraffin: "C32",
    abrsaive: "C33",
    isViscous: "C34",
    surfaceFrequency: "H32",
    KVAavailable: "H33",
    maxOperatingFrequency: "H34",
    downholeSensors: "H35",
    wellHeadType: "H36",
    penetratorType: "H37",
    client: "C5",
    clientState: "C6",
    clientCountry: "C7",
    clientWellName: "C8",
    clientField: "C9",
    clientContact: "H5",
    clientPhone: "H6",
    clientCell: "H7",
    clientEmail: "H8",
    clientDate: "H9",
    notes: "A39"
  };
  const wellData = Object.entries(wellDataMap)
    .filter(item => allCells[item[1]]?.v !== undefined)
    .reduce(
      (acc, [key, cellKey]) =>
        Object.assign(acc, {
          [key]: allCells[cellKey]?.v
        }),
      {} as Record<keyof typeof wellDataMap, string>
    );
  const units = Object.entries(unitsMap).reduce(
    (acc, [key, cellKey]) => {
      let unit;
      switch (allCells[cellKey]?.v) {
        case "Feet":
          unit = LengthUnits.Feet;
          break;
        case "Meters":
          unit = LengthUnits.Meters;
          break;
        case "Degree Fahrenheit":
          unit = TemperatureUnits.Fahrenheit;
          break;
        case "Degree Centigrade":
          unit = TemperatureUnits.Celsius;
          break;
        case "PSI":
          unit = PressureUnits.PoundsPerSquareInchAbsolute;
          break;
        case "kPa":
          unit = PressureUnits.KiloPascals;
          break;
        case "Atm":
          unit = PressureUnits.Atm;
          break;
        case "kgf/cm2":
          unit = PressureUnits.KilogramsPerSquareCentiMeter;
          break;
        case "BPD":
          unit = FlowRateUnits.BarrelsPerDay;
          break;
        case "GPM":
          unit = FlowRateUnits.GallonsPerMinute;
          break;
        case "Liter / M":
          unit = FlowRateUnits.LitersPerMinute;
          break;
        case "M3 / D":
          unit = FlowRateUnits.CubicMetersPerDay;
          break;
        case "M3 / H":
          unit = FlowRateUnits.CubicMetersPerHour;
          break;
        case "GOR - scf/bbl":
        case "GLR - scf/bbl":
          unit = LiquidRatioUnits.StandardCubicFootPerStockTankBarrel;
          break;
        case "GOR - M3/M3":
        case "GLR - M3/M3":
          unit = LiquidRatioUnits.CubicMetersPerCubicMeter;
          break;
        case "Mcf / D":
          unit = FlowRateUnits.ThousandCubicFeetPerDay;
          break;
        default:
          throw new TypeError("Invalid unit string");
      }
      return Object.assign(acc, {
        [key]: unit
      });
    },
    {} as Record<keyof typeof unitsMap, ConvertableUnit>
  );
  const searchField: keyof Tubings =
    allCells.C12.v === "mm - kG/M" ? "metric_name" : "imperial_name";
  const selectedTubing = tubingSearch?.find(
    tube => wellData.tubing === tube[searchField]
  );
  const selectedCasing = casingSearch?.find(
    cas => wellData.casingSize === cas[searchField]
  );
  if (selectedTubing) {
    wellPropertiesFormData.r_top_tubing =
      selectedTubing as FormItems["wellProperties"]["data"]["value"]["r_top_tubing"];
    wellPropertiesFormData.top_tubing_id = selectedTubing?.id;
  }
  if (selectedCasing) {
    wellPropertiesFormData.r_top_casing =
      selectedCasing as FormItems["wellProperties"]["data"]["value"]["r_top_casing"];
    wellPropertiesFormData.top_casing_id = selectedCasing?.id;
  }
  const waterCutRatio = Number(wellData.percentWaterCut) / 100;
  const waterFlowRateStbD = Number(wellData.testLiquidRate) * waterCutRatio;
  const oilFlowRateStbD = Number(wellData.testLiquidRate) * 1 - waterCutRatio;
  Object.assign<
    typeof wellPropertiesFormData,
    Partial<NonNullable<WellProperties>>
  >(wellPropertiesFormData, {
    top_of_perforations_measured_depth_feet: convert(
      Number(wellData.topPerfs),
      units.lengthUnit,
      LengthUnits.Feet
    ),
    top_of_perforations_true_vertical_depth_feet: convert(
      Number(wellData.topPerfs),
      units.lengthUnit,
      LengthUnits.Feet
    ),
    top_tubing_length_feet: convert(
      Number(wellData.tubingLength),
      units.lengthUnit,
      LengthUnits.Feet
    ),
    top_casing_length_feet: convert(
      Number(wellData.topPerfs),
      units.lengthUnit,
      LengthUnits.Feet
    ),
    well_test_datum_point_measured_depth_feet: convert(
      Number(wellData.datumPoint),
      units.lengthUnit,
      LengthUnits.Feet
    ),
    bottomhole_temperature_fahrenheit: convert(
      Number(wellData.tempAtDatum),
      units.temperatureUnit,
      TemperatureUnits.Fahrenheit
    ),
    surface_temperature_fahrenheit: convert(
      Number(wellData.tempAtWellHead),
      units.temperatureUnit,
      TemperatureUnits.Fahrenheit
    ),
    well_test_static_bottom_hole_pressure_psia: convert(
      Number(wellData.staticPr),
      units.pressureUnit,
      PressureUnits.PoundsPerSquareInchAbsolute
    ),
    well_test_producing_bottom_hole_pressure_psia: convert(
      Number(wellData.producingPwf),
      units.pressureUnit,
      PressureUnits.PoundsPerSquareInchAbsolute
    ),
    bubble_point_pressure_psia: convert(
      Number(wellData.bubblePointPressure),
      units.pressureUnit,
      PressureUnits.PoundsPerSquareInchAbsolute
    ),
    well_test_water_rate_stb_day: convert(
      waterFlowRateStbD,
      units.liquidFlowUnit,
      FlowRateUnits.BarrelsPerDay
    ),
    well_test_oil_rate_stb_day: convert(
      Number(oilFlowRateStbD),
      units.liquidFlowUnit,
      FlowRateUnits.BarrelsPerDay
    ),
    well_test_gas_rate_mscf_day: convert(
      Number(wellData.gasRate),
      units.gasFlowUnit,
      FlowRateUnits.ThousandCubicFeetPerDay
    ),
    gas_specific_gravity: wellData.gasGravity,
    oil_gravity_api: wellData.oilGravity,
    water_specific_gravity: wellData.waterGravity,
    h2s_percent: wellData.percentH2S,
    co2_percent: wellData.percentCO2,
    n2_percent: wellData.percentN2
  });
  Object.assign(parametersFormData, {
    intake_packer_installed: wellData.packerAbovePump === "Y",
    vsd_input_voltage: wellData.surfaceVoltage,
    surface_drive_incoming_power_volts: wellData.surfaceVoltage,
    bottom_tubing_pressure_psia: wellData.tubingPressure,
    bottom_casing_pressure_psia: wellData.casingPressure,
    target_liquid_rate_std_barrel_day: wellData.desiredFlow
  });
  Object.assign(equipmentFormData, {
    use_vsd: wellData.surfaceController === "V"
  });
  const noteVariables = [
    { name: "Corrosive", value: wellData.corrosive },
    { name: "Scale", value: wellData.scale },
    { name: "Paraffin", value: wellData.paraffin },
    { name: "Abrsaive", value: wellData.abrsaive },
    { name: "Surface Frequency", value: wellData.surfaceFrequency },
    { name: "KVA Available", value: wellData.KVAavailable },
    { name: "Max Operating Frequency", value: wellData.maxOperatingFrequency },
    { name: "Downhole Sensors", value: wellData.downholeSensors },
    { name: "Well-Head Type", value: wellData.wellHeadType },
    { name: "Penetrator Type", value: wellData.penetratorType },
    { name: "Is Viscous", value: wellData.isViscous },
    { name: "Customer", value: wellData.client },
    { name: "State", value: wellData.clientState },
    { name: "Country", value: wellData.clientCountry },
    { name: "Well Name", value: wellData.clientWellName },
    { name: "Field", value: wellData.clientField },
    { name: "Contact", value: wellData.clientContact },
    { name: "Phone", value: wellData.clientPhone },
    { name: "Cell", value: wellData.clientCell },
    { name: "Email", value: wellData.clientEmail },
    { name: "Date", value: wellData.clientDate },
    { name: "Notes", value: wellData.notes }
  ];
  const notes = noteVariables
    .filter(variable => variable.value)
    .map(variable => `${variable.name}: ${variable.value}`)
    .join(", ");
  if (notes.length > 0) {
    Object.assign(scenarioFormData, {
      notes
    });
  }
  return {
    equipmentFormData,
    scenarioFormData,
    parametersFormData,
    wellPropertiesFormData
  };
}
function validate(worksheet: WorkSheet) {
  const requiredHeaderFields: Record<string, string> = {
    A1: "Well Data Sheet"
  };
  for (const key in requiredHeaderFields) {
    const cell = worksheet[key];
    if (!cell || cell.v !== requiredHeaderFields[key]) {
      return false;
    }
  }
  return true;
}
